/* eslint-disable import/no-anonymous-default-export */
import { Collapse } from "@mui/material";
import { SBTranslate } from "i18n";
import { SBBox, SBGrid } from "modules/Commons";
import { RecommendationMethodology } from "modules/InventoryConfig/models/recommendationMethodology";
import { useContext, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { TipsContext } from "views/ConfigsView/ConfigTips";
import ConfigGroupmentDivider from "../ConfigGroupmentDivider";
import ConfigTextField from "../ConfigTextField";
import ConfigRecommendationMethodology from "./ConfigRecommendationMethodology";

export default ({ fetchPropError }: any) => {
	const maxCSLValue = 100;
	const { getValues, setValue } = useFormContext();
	const tipsContext = useContext(TipsContext);
	const percentageText = <>%</>;
	const multiplyerAdornment = <>x</>;
	const daysText = <>{SBTranslate("day", { count: 2 })}</>;




	const [forecast, setForecast] = useState<number | "">(
		getValues("forecastAdjustment") ? getValues("forecastAdjustment") : ""
	);
	const [virtualStock, setVirtualStock] = useState<number | "">(
		getValues("virtualStock") ? +(getValues("virtualStock") * 100).toFixed(0) : ""
	);
	const [virtualStockPeriod, setVirtualStockPeriod] = useState<number | "">(
		getValues("virtualStockPeriod") ? getValues("virtualStockPeriod") : ""
	);
	const [csl, setCSL] = useState<number | "">(
		getValues("csl") ? +(getValues("csl") * 100).toFixed(0) : ""
	);
	const [minCSL, setMinCSL] = useState<number | "">(
		getValues("minCSL") ? +(getValues("minCSL") * 100).toFixed(0) : ""
	);
	const [maxCSL, setMaxCSL] = useState<number | "">(
		getValues("maxCSL") ? +(getValues("maxCSL") * 100).toFixed(0) : ""
	);
	const [minDOH, setMinDOH] = useState<number | "">(
		getValues("minDOH") ? getValues("minDOH") : ""
	);
	const [maxDOH, setMaxDOH] = useState<number | "">(
		getValues("maxDOH") ? getValues("maxDOH") : ""
	);
	const [excessDOH, setExcessDOH] = useState<number | "">(
		getValues("excessDOH") ? getValues("excessDOH") : ""
	);

	const [recommendationMethodology, setRecommendationMethodology] = useState<RecommendationMethodology | null>(
		getValues("recommendationMethodology") ?? null
	);
	const isCSL = (recommendationMethodology ?? RecommendationMethodology.CSL) === RecommendationMethodology.CSL;

	const onSetRecommendationMethodology = (v: RecommendationMethodology | null) => {
		setValue("recommendationMethodology", v);
		setRecommendationMethodology(v);
		if (v === RecommendationMethodology.DOH) {
			setCSL("");
			setMinCSL("");
			setMaxCSL("");
			setInvertedCSL("");
			setInvertedMinCSL("");
			setInvertedMaxCSL("");
		} else {
			setMinDOH("");
			setMaxDOH("");
			setExcessDOH("");
		}
	};


	const [invertedCSL, setInvertedCSL] = useState<number | "">(
		getValues("csl") ? maxCSLValue - +csl : ""
	);
	const [invertedMinCSL, setInvertedMinCSL] = useState<number | "">(
		getValues("minCSL") ? maxCSLValue - +minCSL : ""
	);
	const [invertedMaxCSL, setInvertedMaxCSL] = useState<number | "">(
		getValues("maxCSL") ? maxCSLValue - +maxCSL : ""
	);

	const updateInvertedCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setInvertedCSL, setCSL);
	const updateInvertedMinCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setInvertedMinCSL, setMinCSL);
	const updateInvertedMaxCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setInvertedMaxCSL, setMaxCSL);

	const updateCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setCSL, setInvertedCSL);
	const updateMinCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setMinCSL, setInvertedMinCSL);
	const updateMaxCSL = (value: string) =>
		verifyLimit(value, maxCSLValue, setMaxCSL, setInvertedMaxCSL);
	const updateForecast = (value: string) =>
		setForecast(value.positiveNumber());
	const updateVirtualStock = (value: string) =>
		setVirtualStock(value.positiveNumber());
	const updateVirtualStockPeriod = (value: string) =>
		setVirtualStockPeriod(value.positiveNumber());
	const updateMinDOH = (value: string) =>
		setMinDOH(value.positiveNumber());
	const updateMaxDOH = (value: string) =>
		setMaxDOH(value.positiveNumber());
	const updateExcessDOH = (value: string) =>
		setExcessDOH(value.positiveNumber());


	const verifyLimit = (
		value: string,
		maxValue: number,
		func: any,
		oppositeFunc: any
	) => {
		if (!value) {
			func("");
			oppositeFunc("");
			return;
		}

		const v = Number(value.naturalNumber());
		if (v > maxValue) {
			func(maxValue);
			oppositeFunc(0);
		} else {
			func(v);
			oppositeFunc(maxValue - v);
		}
	};

	useEffect(() => {
		setValue("forecastAdjustment", forecast);
		setValue("virtualStock", virtualStock !== null && virtualStock !== "" ? virtualStock / 100 : null);
		setValue("virtualStockPeriod", virtualStockPeriod);
		setValue("csl", csl !== null && csl !== "" ? csl / 100 : null);
		setValue(
			"minCSL",
			minCSL !== null && minCSL !== "" ? minCSL / 100 : null
		);
		setValue(
			"maxCSL",
			maxCSL !== null && maxCSL !== "" ? maxCSL / 100 : null
		);
		setValue("minDOH", minDOH !== null && minDOH !== "" ? minDOH : null);
		setValue("maxDOH", maxDOH !== null && maxDOH !== "" ? maxDOH : null);
		setValue("excessDOH", excessDOH !== null && excessDOH !== "" ? excessDOH : null);
	}, [
		csl,
		minCSL,
		maxCSL,
		invertedCSL,
		invertedMinCSL,
		invertedMaxCSL,
		forecast,
		virtualStock,
		virtualStockPeriod,
		minDOH,
		maxDOH,
		excessDOH
	]);

	return (
		<SBBox>
			<ConfigGroupmentDivider title={SBTranslate("recommendationMethodology")} />
			<SBGrid item xs={6} marginBottom={2}>
				<ConfigRecommendationMethodology
					value={recommendationMethodology}
					onUpdate={onSetRecommendationMethodology}
				/>
			</SBGrid>
			<Collapse in={isCSL}>
				<ConfigGroupmentDivider title={SBTranslate("stockNotRunningOut")} />
				<SBGrid container spacing={4}>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							title={SBTranslate("target")}
							type="number"
							value={csl}
							onClick={() => tipsContext.showTip("CSLCSL")}
							onChange={(e) =>
								updateCSL(e.target.value.naturalNumber())
							}
							endAdornment={percentageText}
							helperText={fetchPropError("csl")}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							title={SBTranslate("excess")}
							type="number"
							value={maxCSL}
							onClick={() => {
								tipsContext.showTip("CSLMaxCSL");
							}}
							onChange={(e) =>
								updateMaxCSL(e.target.value.naturalNumber())
							}
							endAdornment={percentageText}
							helperText={fetchPropError("maxCSL")}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							value={minCSL}
							type="number"
							onClick={() => {
								tipsContext.showTip("CSLMinCSL");
							}}
							onChange={(e) =>
								updateMinCSL(e.target.value.naturalNumber())
							}
							endAdornment={percentageText}
							title={SBTranslate("scarcity")}
							helperText={fetchPropError("minCSL")}
						/>
					</SBGrid>
				</SBGrid>
				<ConfigGroupmentDivider title={SBTranslate("stockRunningOut")} />
				<SBGrid container spacing={4}>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							title={SBTranslate("target")}
							type="number"
							endAdornment={percentageText}
							value={invertedCSL}
							onClick={() => {
								tipsContext.showTip("CSLInvertedCSL");
							}}
							onChange={(e) =>
								updateInvertedCSL(e.target.value.naturalNumber())
							}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							title={SBTranslate("excess")}
							type="number"
							endAdornment={percentageText}
							value={invertedMaxCSL}
							onClick={() => {
								tipsContext.showTip("CSLInvertedMaxCSL");
							}}
							onChange={(e) =>
								updateInvertedMaxCSL(e.target.value.naturalNumber())
							}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							title={SBTranslate("scarcity")}
							type="number"
							endAdornment={percentageText}
							value={invertedMinCSL}
							onClick={() => {
								tipsContext.showTip("CSLInvertedMinCSL");
							}}
							onChange={(e) =>
								updateInvertedMinCSL(e.target.value.naturalNumber())
							}
						/>
					</SBGrid>
				</SBGrid>
			</Collapse>
			<Collapse in={!isCSL}>
				<ConfigGroupmentDivider title={SBTranslate("dohConfig")} />
				<SBGrid container spacing={4}>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							type="number"
							value={minDOH}
							onClick={() => tipsContext.showTip("CSLMinDOH")}
							onChange={(e) => updateMinDOH(e.target.value.naturalNumber())}
							title={SBTranslate("minDOH")}
							endAdornment={daysText}
							helperText={fetchPropError("minDOH")}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							type="number"
							value={maxDOH}
							onClick={() => tipsContext.showTip("CSLMaxDOH")}
							onChange={(e) => updateMaxDOH(e.target.value.naturalNumber())}
							title={SBTranslate("maxDOH")}
							endAdornment={daysText}
							helperText={fetchPropError("maxDOH")}
						/>
					</SBGrid>
					<SBGrid item xs={4}>
						<ConfigTextField
							section="csl"
							type="number"
							value={excessDOH}
							onClick={() => tipsContext.showTip("CSLExcessDOH")}
							onChange={(e) => updateExcessDOH(e.target.value.naturalNumber())}
							title={SBTranslate("excessDOH")}
							endAdornment={daysText}
							helperText={fetchPropError("excessDOH")}
						/>
					</SBGrid>
				</SBGrid>
			</Collapse>
			<ConfigGroupmentDivider title={SBTranslate("forecastAjustment")} />
			<SBGrid container spacing={4}>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="CSL"
						type="number"
						title={SBTranslate("commons.forecast")}
						endAdornment={multiplyerAdornment}
						value={forecast}
						onChange={(e) => updateForecast(e.target.value)}
						onClick={() => {
							tipsContext.showTip("CSLForecastAjustment");
						}}
					/>
				</SBGrid>
			</SBGrid>
			<ConfigGroupmentDivider title={SBTranslate("demandAnomalyDetection")} />
			<SBGrid container spacing={4}>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="csl"
						title={SBTranslate("limit")}
						value={virtualStock}
						type="number"
						onClick={() => tipsContext.showTip(("CSLVirtualStock"))}
						onChange={(e) => updateVirtualStock(e.target.value.naturalNumber())}
						endAdornment={percentageText}
						helperText={fetchPropError("virtualStock")}
					/>
				</SBGrid>
				<SBGrid item xs={4}>
					<ConfigTextField
						section="csl"
						title={SBTranslate("period")}
						value={virtualStockPeriod}
						type="number"
						onClick={() => tipsContext.showTip(("CSLVirtualStockPeriod"))}
						onChange={(e) => updateVirtualStockPeriod(e.target.value.naturalNumber())}
						endAdornment={SBTranslate("days")}

					/>
				</SBGrid>
			</SBGrid>
		</SBBox>
	);
};
